import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { flowsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, GitBranch, Play, Pause, Trash2, Edit, Zap } from 'lucide-react';
import { toast } from 'sonner';

const SEGMENTO_LABELS = {
    geral: 'Geral', mulheres_com_proposito: 'Mulheres com Propósito',
    mesa_de_ester: 'Mesa de Ester', luto_ao_recomeco: 'Do Luto ao Recomeço',
    devocional: 'Devocional', musica: 'Música', livro: 'Livro',
};

export const Flows = () => {
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const res = await flowsAPI.list();
            setFlows(res.data || []);
        } catch { toast.error('Erro ao carregar fluxos'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggle = async (flow) => {
        try {
            if (flow.active) { await flowsAPI.deactivate(flow.id); toast.success('Fluxo pausado'); }
            else             { await flowsAPI.activate(flow.id);   toast.success('Fluxo ativado'); }
            load();
        } catch { toast.error('Erro ao alterar status'); }
    };

    const del = async (id) => {
        if (!window.confirm('Excluir este fluxo?')) return;
        try { await flowsAPI.delete(id); toast.success('Excluído'); load(); }
        catch { toast.error('Erro ao excluir'); }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-100 flex items-center gap-2">
                        <GitBranch className="w-6 h-6 text-amber-500" /> Fluxos de Automação
                    </h1>
                    <p className="text-stone-400 text-sm mt-1">Canvas visual para criar sequências de mensagens</p>
                </div>
                <Button onClick={() => navigate('/flows/novo')} className="bg-amber-700 hover:bg-amber-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Novo Fluxo
                </Button>
            </div>

            {loading ? (
                <div className="text-stone-400 text-center py-20">Carregando...</div>
            ) : flows.length === 0 ? (
                <Card className="bg-stone-900 border-stone-800">
                    <CardContent className="py-16 text-center">
                        <Zap className="w-10 h-10 text-stone-600 mx-auto mb-3" />
                        <p className="text-stone-400">Nenhum fluxo criado ainda.</p>
                        <Button className="mt-4 bg-amber-700 hover:bg-amber-600 text-white"
                            onClick={() => navigate('/flows/novo')}>Criar primeiro fluxo</Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {flows.map(flow => (
                        <Card key={flow.id} className="bg-stone-900 border-stone-800 hover:border-stone-700 transition-all">
                            <CardHeader className="pb-2">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="text-stone-100 text-base">{flow.name}</CardTitle>
                                        <p className="text-xs text-stone-500 mt-0.5">
                                            {SEGMENTO_LABELS[flow.segmento] || flow.segmento}
                                        </p>
                                    </div>
                                    <Badge className={flow.active
                                        ? 'bg-green-900/40 text-green-300 border border-green-700'
                                        : 'bg-stone-800 text-stone-400 border border-stone-700'}>
                                        {flow.active ? '● Ativo' : '○ Pausado'}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
                                    <span>{flow.nodes?.length || 0} nós</span>
                                    <span>·</span>
                                    <span>{flow.run_count || 0} execuções</span>
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline"
                                        className="flex-1 border-stone-700 text-stone-300 hover:text-stone-100 text-xs"
                                        onClick={() => navigate(`/flows/${flow.id}`)}>
                                        <Edit className="w-3 h-3 mr-1" /> Editar
                                    </Button>
                                    <Button size="sm" variant="outline"
                                        className={`border-stone-700 text-xs ${flow.active ? 'text-yellow-400' : 'text-green-400'}`}
                                        onClick={() => toggle(flow)}>
                                        {flow.active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                                    </Button>
                                    <Button size="sm" variant="outline"
                                        className="border-stone-700 text-red-400 hover:text-red-300 text-xs"
                                        onClick={() => del(flow.id)}>
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Flows;
